import React, { useContext, useEffect, useState } from 'react';
import {
  CheckCircle,
  Email,
  FilterCenterFocus,
  GpsFixed,
  HomeWork,
  LocalPhone,
  Print,
  Public,
  RadioButtonUnchecked,
  Receipt,
  Room,
  Language
} from '@mui/icons-material';
import { Box, Checkbox, FormControlLabel, Switch, Typography } from '@mui/material';
import {
  IAddress,
  ICountry,
  IRegion,
  useAddAddressMutation,
  useCountriesQuery,
  useRegionsQuery,
  useUpdateAddressMutation
} from '../../../../generated/graphql';
import { Colors } from '../../../../utils/theme';
import CancelButton from '../../../Common/Buttons/CancelButton';
import EditButton from '../../../Common/Buttons/EditButton';
import SaveButton from '../../../Common/Buttons/SaveButton';
import AutocompleteComponent from '../../../Common/Fields/AutocompleteComponent';
import InputComponent from '../../../Common/Fields/InputComponent';
import { canUserEdit } from '../../../../utils/rightUtils';
import { AppContext } from '../../../../AppContext';
import { Types } from '../../../../enums/TypeEnums';
import { ApolloError } from '@apollo/client';
import add_road from '../../../../assets/images/icons/add_road.svg';
import add_road_grey from '../../../../assets/images/icons/add_road_grey.svg';
import { Actions } from '../../../../enums/ActionEnums';

interface IProps {
  addressInput?: IAddress;
  handleClose: () => void;
  updateList: (address: IAddress) => void;
  companyId?: number;
}

interface IErrors {
  name: string | null;
  street: string | null;
  country: string | null;
  city: string | null;
  region: string | null;
  updateError?: ApolloError;
}

enum FormErrorEnums {
  Name = 'Address name too short!',
  Street = 'Street too short!',
  Country = 'Country is required!',
  City = 'City too short!',
  Region = 'Region is required!'
}

const defaultErrorsObj: IErrors = {
  name: null,
  street: null,
  country: null,
  city: null,
  region: null
};

const AddressForm: React.FunctionComponent<IProps> = ({
  addressInput,
  handleClose,
  updateList,
  companyId
}: IProps) => {
  const [editable, setEditable] = useState(addressInput === undefined);
  const { dispatch, state } = useContext(AppContext);
  const canEdit = canUserEdit(state.user, Types.Company);
  const [regionOptions, setRegionOptions] = useState([]);
  const [countyOptions, setCountryOptions] = useState([]);
  const countries = useCountriesQuery();
  const regions = useRegionsQuery();
  const [formErrors, setFormErrors] = useState(defaultErrorsObj);
  const [name, setName] = useState(addressInput?.name || '');
  const [street, setStreet] = useState(addressInput?.street || '');
  const [city, setCity] = useState(addressInput?.city || '');
  const [zip, setZip] = useState(addressInput?.zip || '');
  const [phone, setPhone] = useState(addressInput?.phone || '');
  const [fax, setFax] = useState(addressInput?.fax || '');
  const [email, setEmail] = useState(addressInput?.email || '');
  const [vat_number, setVat] = useState(addressInput?.vat_number || '');
  const [vat_country_code, setVatCountryCode] = useState(addressInput?.vat_country_code || '');
  const [is_website, setIsWebsite] = useState(addressInput?.is_website);
  const [is_main, setIsMain] = useState(addressInput?.is_main);
  const [is_nav, setIsNav] = useState(addressInput?.is_nav);
  const [country, setCountry] = useState<ICountry | null>(addressInput?.country || null);
  const [region, setRegion] = useState<IRegion | null>(addressInput?.country.region || null);
  const [updateAddress] = useUpdateAddressMutation();
  const [addAddress] = useAddAddressMutation();

  useEffect(() => {
    if (countries.data && countries.data.result) {
      setCountryOptions(countries.data.result as never[]);
    }
    if (regions.data && regions.data.result) {
      setRegionOptions(regions.data.result as never[]);
    }
  }, [countries.data, regions.data]);

  const handleCancel = () => {
    if (editable && addressInput) {
      setName(addressInput.name);
      setStreet(addressInput.street);
      setCountry(addressInput.country || null);
      setCity(addressInput.city);
      setZip(addressInput.zip || '');
      setRegion(addressInput.country.region || null);
      setPhone(addressInput.phone || '');
      setFax(addressInput.fax || '');
      setEmail(addressInput.email || '');
      setVat(addressInput.vat_number || '');
      setVatCountryCode(addressInput.vat_country_code || '');
      setIsWebsite(addressInput.is_website);
      setIsMain(addressInput.is_main);
      setIsNav(addressInput.is_nav);
      setEditable(false);
      setFormErrors(defaultErrorsObj);
    } else {
      handleClose();
    }
  };

  const handleNameChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { value } = event.currentTarget;
    if (!value || value.length < 2) {
      setFormErrors({
        ...formErrors,
        name: FormErrorEnums.Name
      });
    } else {
      setFormErrors({ ...formErrors, name: null });
    }
    setName(value);
  };

  const handleCityChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { value } = event.currentTarget;
    if (!value || value.length < 2) {
      setFormErrors({
        ...formErrors,
        city: FormErrorEnums.City
      });
    } else {
      setFormErrors({ ...formErrors, city: null });
    }
    setCity(value);
  };

  const handleStreetChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { value } = event.currentTarget;
    if (!value || value.length < 2) {
      setFormErrors({
        ...formErrors,
        street: FormErrorEnums.Street
      });
    } else {
      setFormErrors({ ...formErrors, street: null });
    }
    setStreet(value);
  };

  const handleCountryChange = (_: unknown, newValue: React.SetStateAction<ICountry | null>) => {
    if (newValue) {
      const country = newValue as ICountry;
      setFormErrors({ ...formErrors, country: null, region: null });
      const region = regions.data?.result.find((r) => r?.id === country.region?.id) as IRegion;
      setRegion(region);
    } else {
      setFormErrors({ ...formErrors, country: FormErrorEnums.Country });
    }
    setCountry(newValue);
  };

  const handleRegionChange = (_: unknown, newValue: React.SetStateAction<IRegion | null>) => {
    if (newValue) {
      const region = newValue as IRegion;
      const possibleCountryOptions = countries.data?.result.filter(
        (c) => c?.region?.id === region.id
      );
      setCountryOptions(possibleCountryOptions as never[]);
      setFormErrors({ ...formErrors, region: null });
    } else {
      setCountryOptions(countries.data?.result as never[]);
      setFormErrors({ ...formErrors, region: FormErrorEnums.Region });
    }
    setCountry(null);
    setRegion(newValue);
  };

  const handleSubmit = () => {
    const errors: IErrors = {
      name: null,
      street: null,
      country: null,
      city: null,
      region: null
    };
    let hasErrors = false;
    if (!name || name.length < 2) {
      errors.name = FormErrorEnums.Name;
      hasErrors = true;
    }
    if (!street || street.length < 2) {
      errors.street = FormErrorEnums.Street;
      hasErrors = true;
    }
    if (!country) {
      errors.country = FormErrorEnums.Country;
      hasErrors = true;
    }
    if (!city || city.length < 2) {
      errors.city = FormErrorEnums.City;
      hasErrors = true;
    }
    if (hasErrors) {
      setFormErrors(errors);
    } else {
      handleSave(addressInput === undefined ? 'create' : 'update');
    }
  };

  const handleSave = (type: 'create' | 'update') => {
    const countryInput = country as ICountry;
    const input = {
      zip,
      fax,
      country_id: parseInt(countryInput.id),
      name,
      street,
      city,
      vat_number,
      phone,
      email,
      is_main,
      is_nav,
      is_website,
      vat_country_code
    };

    if (type === 'create') {
      addAddress({
        variables: {
          company_id: companyId as number,
          input
        },
        update(_, { data }) {
          if (data?.result) {
            updateList(data.result);
            handleClose();
            dispatch({
              type: Actions.ShowMessage,
              payload: {
                severity: 'success',
                text: `You have successfully added ${data.result.name} to the list of addresses`
              }
            });
          }
        }
      }).catch((e) =>
        dispatch({
          type: Actions.ShowMessage,
          payload: {
            severity: 'error',
            text: e.message,
            autoHide: 6000
          }
        })
      );
    } else {
      updateAddress({
        variables: {
          id: parseInt((addressInput as IAddress).id),
          input
        },
        update(_, { data }) {
          if (data?.result) {
            handleClose();
            dispatch({
              type: Actions.ShowMessage,
              payload: {
                severity: 'success',
                text: `You have successfully updated ${data.result.name}'s address data`
              }
            });
            updateList(data?.result);
          }
        }
      }).catch((e) =>
        dispatch({
          type: Actions.ShowMessage,
          payload: {
            severity: 'error',
            text: e.message,
            autoHide: 6000
          }
        })
      );
    }
  };
  return (
    <Box sx={{ padding: '80px', position: 'relative' }}>
      {addressInput && (
        <Box sx={{ position: 'absolute', top: '24px', right: '24px' }}>
          <EditButton
            id="edit-address-button"
            onClick={() => setEditable(true)}
            disabled={!canEdit || editable}
            editButtonColor={!canEdit || editable ? Colors.gray : Colors.skyBlue}
          />
        </Box>
      )}
      {formErrors.updateError && (
        <Box style={{ textAlign: 'center' }}>
          <Typography color="error">{formErrors.updateError.message}</Typography>
        </Box>
      )}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
          columnGap: '7.2%',
          paddingX: '5%'
        }}
      >
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
            gap: '35px 14%'
          }}
        >
          <InputComponent
            id="address-name"
            label="Address Name"
            value={name}
            onChange={handleNameChange}
            disabled={!editable}
            errorMsg={formErrors.name}
            additionalStyle={{ gridColumn: 'span 2' }}
            startAdornment={
              <HomeWork
                sx={{
                  mr: '12px',
                  color: editable ? Colors.main : Colors.gray
                }}
              />
            }
          />
          <InputComponent
            id="street"
            label="Street"
            value={street}
            onChange={handleStreetChange}
            disabled={!editable}
            errorMsg={formErrors.street}
            additionalStyle={{ gridColumn: 'span 2' }}
            startAdornment={
              editable ? (
                <img
                  src={add_road}
                  alt="addRoadButton"
                  style={{
                    marginRight: '12px'
                  }}
                />
              ) : (
                <img
                  src={add_road_grey}
                  alt="addRoadButton"
                  style={{
                    marginRight: '12px'
                  }}
                />
              )
            }
          />
          <AutocompleteComponent
            id="countries"
            label="Country"
            options={countyOptions}
            getOptionLabel={(option: ICountry) => option?.name}
            disabled={!editable}
            values={country}
            onChange={handleCountryChange}
            errorMsg={formErrors.country}
            additionalStyle={{ gridColumn: 'span 2' }}
            startAdornment={
              <Public
                sx={{
                  mr: '12px',
                  color: editable ? Colors.main : Colors.gray
                }}
              />
            }
          />
          <InputComponent
            id="city"
            label="City"
            value={city}
            onChange={handleCityChange}
            disabled={!editable}
            errorMsg={formErrors.city}
            startAdornment={
              <Room
                sx={{
                  mr: '12px',
                  color: editable ? Colors.main : Colors.gray
                }}
              />
            }
          />
          <InputComponent
            id="zip"
            label="ZIP Code"
            value={zip ?? ''}
            onChange={(e) => setZip(e.target.value)}
            disabled={!editable}
            startAdornment={
              <FilterCenterFocus
                sx={{
                  mr: '12px',
                  color: editable ? Colors.main : Colors.gray
                }}
              />
            }
          />
          <AutocompleteComponent
            id="regions"
            label="Region"
            options={regionOptions}
            getOptionLabel={(option: IRegion) => option?.name}
            disabled={!editable}
            values={region}
            onChange={handleRegionChange}
            errorMsg={formErrors.region}
            additionalStyle={{ gridColumn: 'span 2' }}
            startAdornment={
              <GpsFixed
                sx={{
                  mr: '12px',
                  color: editable ? Colors.main : Colors.gray
                }}
              />
            }
          />
        </Box>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
            gap: '35px 5%'
          }}
        >
          <InputComponent
            id="phone"
            label="Phone (direct)"
            value={phone ?? ''}
            onChange={(e) => setPhone(e.target.value)}
            disabled={!editable}
            startAdornment={
              <LocalPhone
                sx={{
                  mr: '12px',
                  color: editable ? Colors.main : Colors.gray
                }}
              />
            }
          />
          <InputComponent
            id="fax"
            label="Fax (direct)"
            value={fax ?? ''}
            onChange={(e) => setFax(e.target.value)}
            disabled={!editable}
            startAdornment={
              <Print
                sx={{
                  mr: '12px',
                  color: editable ? Colors.main : Colors.gray
                }}
              />
            }
          />
          <InputComponent
            id="email"
            label="Email (direct)"
            value={email ?? ''}
            onChange={(e) => setEmail(e.target.value)}
            disabled={!editable}
            additionalStyle={{ gridColumn: 'span 2' }}
            startAdornment={
              <Email
                sx={{
                  mr: '12px',
                  color: editable ? Colors.main : Colors.gray
                }}
              />
            }
          />
          <InputComponent
            id="vat-code"
            label="VAT Country Code"
            value={vat_country_code ?? ''}
            onChange={(e) => setVatCountryCode(e.target.value)}
            disabled={!editable}
            startAdornment={
              <Language
                sx={{
                  mr: '12px',
                  color: editable ? Colors.main : Colors.gray
                }}
              />
            }
          />
          <InputComponent
            id="vat"
            label="VAT Number"
            value={vat_number ?? ''}
            onChange={(e) => setVat(e.target.value)}
            disabled={!editable}
            startAdornment={
              <Receipt
                sx={{
                  mr: '12px',
                  color: editable ? Colors.main : Colors.gray
                }}
              />
            }
          />
          <FormControlLabel
            control={
              <Switch
                id="is-website"
                checked={is_website || false}
                onChange={() => setIsWebsite(!is_website)}
              />
            }
            label="Show address on EPRA website"
            labelPlacement="start"
            sx={{
              color: editable ? Colors.main : Colors.gray,
              gridColumn: 'span 2',
              display: 'flex',
              justifyContent: 'flex-end',
              ml: 0
            }}
            disabled={!editable}
          />
          <FormControlLabel
            control={
              <Checkbox
                id="is-main"
                icon={<RadioButtonUnchecked />}
                checkedIcon={<CheckCircle />}
                checked={is_main || false}
                onChange={() => setIsMain(!is_main)}
                color="success"
              />
            }
            label="Main Address"
            labelPlacement="end"
            style={{ color: editable ? Colors.main : Colors.gray }}
            disabled={!editable}
          />
          <FormControlLabel
            control={
              <Checkbox
                id="is-nav"
                icon={<RadioButtonUnchecked />}
                checkedIcon={<CheckCircle />}
                checked={is_nav || false}
                onChange={() => setIsNav(!is_nav)}
                color="success"
              />
            }
            label="Nav Address"
            labelPlacement="end"
            style={{
              color: editable ? Colors.main : Colors.gray,
              marginLeft: '-25%'
            }}
            disabled={!editable}
          />
        </Box>
      </Box>
      {editable && (
        <Box className="centralize" sx={{ marginTop: '140px' }}>
          <CancelButton id="cancel-button" onClick={handleCancel} text="Cancel" />
          <SaveButton id="save-button" onClick={handleSubmit} text="Save" />
        </Box>
      )}
    </Box>
  );
};

export default AddressForm;
